import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { AppConfig } from "@/types/app";
import { Lock, AlertCircle } from "lucide-react";

interface AccessDenialDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onRequestAccess?: () => void;
  appConfig: AppConfig | null;
  redirectUrl?: string;
}

export function AccessDenialDialog({
  isOpen,
  onClose,
  onRequestAccess,
  appConfig,
  redirectUrl,
}: AccessDenialDialogProps) {
  const navigate = useNavigate();

  const handleRequestAccess = () => {
    if (onRequestAccess) {
      onRequestAccess();
    } else {
      onClose();
      navigate("/profile", {
        state: {
          openManageAccess: true,
        },
      });
    }
  };

  // Extract app name from redirect URL if appConfig is not available
  const getAppNameFromUrl = (url: string | undefined): string => {
    if (!url) return "this application";
    try {
      const urlObj = new URL(url);
      return urlObj.hostname;
    } catch {
      return "this application";
    }
  };

  const appName = appConfig?.name || (redirectUrl ? getAppNameFromUrl(redirectUrl) : "this application");
  const AppIcon = appConfig?.icon;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <div className="flex items-center gap-4 mb-4">
            {AppIcon && appConfig ? (
              <div
                className={`relative w-16 h-16 shrink-0 rounded-2xl flex items-center justify-center ${appConfig.color} shadow-lg`}
              >
                <AppIcon className="w-8 h-8" />
                <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-destructive rounded-full flex items-center justify-center border-2 border-background">
                  <Lock className="w-3 h-3 text-destructive-foreground" />
                </div>
              </div>
            ) : (
              <div className="relative w-16 h-16 shrink-0 rounded-2xl flex items-center justify-center bg-muted">
                <AlertCircle className="w-8 h-8 text-muted-foreground" />
              </div>
            )}
            <div className="flex-1">
              <DialogTitle className="text-2xl font-bold">
                Access Denied
              </DialogTitle>
              <DialogDescription className="text-base mt-1">
                You don't have permission to access {appName}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="rounded-lg border border-border/50 bg-muted/30 p-4">
            <div className="flex items-start gap-3">
              <div className="rounded-full bg-muted p-2 mt-0.5">
                <Lock className="w-4 h-4 text-muted-foreground" />
              </div>
              <div className="flex-1 space-y-1">
                <p className="text-sm font-medium">Why am I seeing this?</p>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Your account doesn't have the required permissions to access{" "}
                  <span className="font-medium text-foreground">{appName}</span>.
                  Contact your administrator or request access below.
                </p>
              </div>
            </div>
          </div>

          {appConfig?.description ? (
            <div className="text-sm text-muted-foreground">
              <span className="font-medium text-foreground">About {appName}:</span>{" "}
              {appConfig.description}
            </div>
          ) : redirectUrl && !appConfig && (
            <div className="text-sm text-muted-foreground">
              <span className="font-medium text-foreground">Requested URL:</span>{" "}
              <span className="break-all">{redirectUrl}</span>
            </div>
          )}
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button
            variant="outline"
            onClick={onClose}
            className="w-full sm:w-auto"
          >
            Close
          </Button>
          <Button
            onClick={handleRequestAccess}
            className="w-full sm:w-auto gradient-primary"
          >
            Request Access
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

